import {
  Alert,
  DatePicker,
  Table,
  Button,
  Input,
  Row,
  Col,
  Popconfirm,
  Modal,
  Tooltip,
  Checkbox,
  Select,
  Switch,
  Spin,
  Empty,
} from 'antd'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import FadeIn from 'src/components/styled/FadeIn'
import styled from 'styled-components/macro'
import { ThemeContext } from 'styled-components'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import {
  deleteSalesOrPurchaseData,
  fetchGrossProfitAndSales,
  sendSalesOrPurchaseData,
  setDateRange,
} from 'src/features/WeeklySalesSlice'
const { RangePicker } = DatePicker
const { Option } = Select
import { grossProfitRangeFromSearch } from 'src/screens/orders/grossProfitRange'
import './grossprofit.css'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import VatToggle from 'src/components/shared/VatToggle'
import {
  DeleteOutlined,
  DownOutlined,
  DownloadOutlined,
  EditOutlined,
  InfoCircleOutlined,
  UpOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import SalesModal from 'src/modals/SalesModal'
import RevenueCategoryList from 'src/screens/orders/RevenueCategoryList'
import SalesSplit from 'src/screens/orders/SalesSplit'
import { canConfigureRevenueCategories } from 'src/utils/revenueCategoryAccess'
import {
  listRevenueCategories,
  readRevenueDay,
  readRevenueDays,
  saveRevenueDay,
} from 'src/utils/revenueCategoryApi'
import {
  isRecordedAsOneTotal,
  revenueSplitExportRows,
  saleFigureLinesWouldReplace,
  uncategorisedRevenue,
} from 'src/utils/revenueSplit'
import {
  previewRevenueLine,
  previewRevenueTotal,
} from 'src/utils/revenueLinePreview'
import MisCostModal from 'src/modals/MiscCostModal'
import PlanLimitModal from '../subscriptions/PlanLimitModal'
import ForecasteddSalesModal from 'src/modals/ForecastedModal'
import useIsBlocked from 'src/hooks/useIsBlocked'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import NotesModal from 'src/modals/NotesModal'
import { ResponsiveLine } from '@nivo/line'
import {
  GP_EXCLUDED_THIS_PERIOD_TOOLTIP,
  GP_OMS_SPEND_AVG_TOOLTIP,
  GP_OMS_SPEND_COLUMN_TOOLTIP,
  formatGpExcludedThisPeriodSummary,
} from 'src/utils/grossProfitOmsSpendCopy'
import { RESPONSIVE_TABLE_SCROLL } from 'src/utils/responsiveTable'
import { API } from 'src/utils/interceptor'
import * as XLSX from 'xlsx'
import { ReactMultiEmail, isEmail } from 'react-multi-email'
import { editVenue } from 'src/features/VenueSlice'
import { Link } from 'react-router-dom'
import { selectVatPreference } from 'src/features/VatPreferenceSlice'
import { pickGpOmsSpend, vatLabelSuffix } from 'src/utils/vatPreference'
import { describeRevenueCategoryMultiplier } from 'src/utils/revenueLinePreview'

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

const formatCurrency = value => {
  const parsed = Number.parseFloat(value)
  const amount = Number.isNaN(parsed) ? 0 : parsed
  return `£${amount.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const formatPercent = value => {
  const parsed = Number.parseFloat(value)
  const amount = Number.isNaN(parsed) ? 0 : parsed
  return `${amount.toFixed(1)}%`
}

const DayRevenueLines = ({ lines, sales, loading }) => {
  if (loading) return <p style={{ margin: '8px 0' }}>Loading this day…</p>
  const rows = Array.isArray(lines) ? lines : []
  if (!rows.length) {
    const total = Number(sales)
    if (!Number.isFinite(total) || total === 0) return null
    return (
      <p style={{ margin: '8px 0' }}>
        {`${formatCurrency(total)} recorded as one total.`}
      </p>
    )
  }
  return (
    <div style={{ margin: '8px 0 12px' }}>
      {rows.map((line, index) => {
        const words = describeRevenueCategoryMultiplier(line.multiplier)
        const entry = words === 'Amount' ? 'Amount' : `Quantity ${words}`
        return (
          <div key={`${line.name}-${index}`}>
            {`${line.name} · ${entry} · ${line.entry} · ${formatCurrency(
              line.calculated,
            )}`}
          </div>
        )
      })}
    </div>
  )
}

const formatAxisCurrency = value => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return '£0'
  if (Math.abs(parsed) >= 1000000) {
    return `£${(parsed / 1000000).toFixed(1)}m`
  }
  if (Math.abs(parsed) >= 1000) {
    return `£${(parsed / 1000).toFixed(1)}k`
  }
  return `£${parsed.toFixed(0)}`
}

const toNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const hexToRgba = (hex, alpha) => {
  if (!hex) return `rgba(0,0,0,${alpha})`
  let normalized = hex.replace('#', '')
  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map(ch => ch + ch)
      .join('')
  }
  const bigint = parseInt(normalized, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const truncateText = (text, maxLength = 50) => {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

/** Prefill inline input: trim float noise, keep decimals (up to 4 dp for entry) */
const formatNumberForInlineInput = raw => {
  if (raw === null || raw === undefined || raw === '') return ''
  const n = Number(raw)
  if (!Number.isFinite(n)) return ''
  if (n === 0 && raw !== 0 && raw !== '0') return ''
  if (Number.isInteger(n)) return String(n)
  return String(Math.round(n * 10000) / 10000).replace(/\.?0+$/, '')
}

const GrossProfit = () => {
  const blocked = useIsBlocked('gross-profit')
  const salesSearch = useLocation().search

  if (blocked) {
    return <PermissionBlockScreen />
  }

  const dispatch = useDispatch()
  const [visibleCalendar, setVisibleCalendar] = useState(false)
  const [showLimitModal, setShowLimitModal] = useState(false)
  const [visibleSalesModal, setVisibleSalesModal] = useState(false)
  const [visibleMiscCostModal, setVisibleMiscCostModal] = useState(false)
  const [visibleForecastModal, setVisibleForecastModal] = useState(false)
  const [visibleNotesModal, setVisibleNotesModal] = useState(false)
  const [selectedNoteDate, setSelectedNoteDate] = useState(null)
  const [existingNote, setExistingNote] = useState(null)
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(true)
  const [activeQuickRange, setActiveQuickRange] = useState('7d')
  const [selectedMetrics, setSelectedMetrics] = useState(['revenue', 'spend'])
  const [weekAverageEnabled, setWeekAverageEnabled] = useState(false)
  const [previousYearEnabled, setPreviousYearEnabled] = useState(false)
  const {
    totalSaleWeek,
    dateRange,
    GP,
    GPExclMisc,
    totalMiscCosts,
    totalSaleAmount,
    excludedThisPeriod,
  } = useSelector(state => state.sale)
  const excludedThisPeriodSummary = formatGpExcludedThisPeriodSummary(
    excludedThisPeriod,
    { formatSpend: formatCurrency },
  )
  const venue = useSelector(state => state.venue?.venue)
  const venueId = useSelector(state => state.venue?.venue?._id)
  const [expandedRevenue, setExpandedRevenue] = useState({})
  const account = useSelector(state => state.auth.account)
  const canConfigureCategories = canConfigureRevenueCategories({
    account,
    venue,
  })
  const vatPreference = useSelector(selectVatPreference)
  const { currentPlan } =
    useSelector(state => state.venue?.venue?.usageLimits) ?? {}

  // Local state for email recipients (matching Settings.js pattern)
  // Initialize from venue data, but use empty array as fallback to avoid new array on every render
  const [gpReportEmails, setGpReportEmails] = useState(
    venue?.gpReportEmails || [],
  )
  // Local state for report frequency; default "none" when unset
  const [gpReportFrequency, setGpReportFrequency] = useState(
    venue?.gpReportFrequency || 'none',
  )
  const [showSaved, setShowSaved] = useState(false)
  const [emailSettingsOpen, setEmailSettingsOpen] = useState(false)
  const defaultDateRange = {
    start: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
    end: dayjs().format('YYYY-MM-DD'),
  }

  const quickRanges = [
    { label: '7d', value: '7d' },
    { label: '30d', value: '30d' },
    { label: '90d', value: '90d' },
    { label: 'This Month', value: 'this-month' },
    { label: '12m', value: '12m' },
    { label: 'Custom', value: 'custom' },
  ]

  const metricOptions = [
    { label: 'Revenue', value: 'revenue' },
    { label: 'Spend', value: 'spend' },
    { label: 'Profit %', value: 'profit' },
  ]
  const theme = useContext(ThemeContext) || {}
  const isLoading = useSelector(state => state.sale.isLoading)
  const [previousYearData, setPreviousYearData] = useState(null)
  const [previousYearStatus, setPreviousYearStatus] = useState({
    loading: false,
    error: null,
  })
  const [revenueCategories, setRevenueCategories] = useState(null)
  const [categoryEdit, setCategoryEdit] = useState(null)
  const [revenueLinesByDate, setRevenueLinesByDate] = useState(null)
  const activeRevenueCategories = useMemo(
    () =>
      (revenueCategories || []).filter(category => category.active !== false),
    [revenueCategories],
  )

  useEffect(() => {
    if (!venueId) return undefined
    let cancelled = false
    setRevenueCategories(null)
    listRevenueCategories(venueId)
      .then(data => {
        if (!cancelled) setRevenueCategories(Array.isArray(data) ? data : [])
        return null
      })
      .catch(() => {
        if (!cancelled) setRevenueCategories([])
      })
    return () => {
      cancelled = true
    }
  }, [venueId])

  useEffect(() => {
    if (
      !venueId ||
      !dateRange?.startDate ||
      !dateRange?.endDate ||
      currentPlan === 'Free' ||
      activeRevenueCategories.length === 0
    ) {
      setRevenueLinesByDate(null)
      return undefined
    }
    let cancelled = false
    readRevenueDays(venueId, dateRange.startDate, dateRange.endDate)
      .then(data => {
        if (cancelled) return null
        const next = {}
        for (const day of data?.days || []) {
          const key = dayjs(day.date).format('YYYY-MM-DD')
          next[key] = Array.isArray(day.revenueLines) ? day.revenueLines : []
        }
        setRevenueLinesByDate(next)
        return null
      })
      .catch(() => {
        if (!cancelled) setRevenueLinesByDate(null)
      })
    return () => {
      cancelled = true
    }
  }, [
    venueId,
    dateRange?.startDate,
    dateRange?.endDate,
    currentPlan,
    activeRevenueCategories.length,
  ])
  const [inlineEditCell, setInlineEditCell] = useState(null)
  const [inlineEditValue, setInlineEditValue] = useState('')
  const [inlineEditError, setInlineEditError] = useState('')
  const inlineCommitRef = useRef(false)

  const revenueColour = theme?.colours?.primaryGreen ?? '#63B967'
  const spendColour = theme?.colours?.omsRed ?? '#EB5757'
  const profitColour = theme?.colours?.iceBlue ?? '#2F80ED'

  const analyticsData = useMemo(() => {
    if (!Array.isArray(totalSaleWeek)) return []
    const today = dayjs().startOf('day')
    return totalSaleWeek
      .slice()
      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
      .map(entry => {
        const date = dayjs(entry.date)
        const revenue = toNumber(entry.sales)
        const miscCosts = toNumber(entry.miscCostPerDay)
        const omsSpend = pickGpOmsSpend(vatPreference, entry)
        const omsSpendExVat = toNumber(entry.purchasesOnOMS)
        const spend = miscCosts + omsSpend
        // Profit stays on the ex-VAT basis so it matches the server's GP%.
        const profitPercent =
          revenue > 0
            ? ((revenue - (miscCosts + omsSpendExVat)) / revenue) * 100
            : 0
        return {
          dateISO: date.format('YYYY-MM-DD'),
          displayDate: date.format('ddd DD MMM'),
          revenue,
          spend,
          profitPercent,
          isFuture: date.isAfter(today),
        }
      })
  }, [totalSaleWeek, vatPreference])

  const notesForDateRange = useMemo(() => {
    if (!Array.isArray(totalSaleWeek) || !dateRange) return []
    return totalSaleWeek
      .filter(entry => {
        if (!entry.notes) return false
        const entryDate = dayjs(entry.date)
        const startDate = dayjs(dateRange.startDate)
        const endDate = dayjs(dateRange.endDate)
        return (
          entryDate.isSameOrAfter(startDate, 'day') &&
          entryDate.isSameOrBefore(endDate, 'day')
        )
      })
      .map(entry => ({
        date: entry.date,
        notes: entry.notes,
        displayDate: dayjs(entry.date).format('D MMM YYYY'),
      }))
      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
  }, [totalSaleWeek, dateRange])

  const previousYearAnalyticsData = useMemo(() => {
    if (!Array.isArray(previousYearData?.salesPerDay)) return []
    return previousYearData.salesPerDay
      .slice()
      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
      .map(entry => {
        const date = dayjs(entry.date)
        const revenue = toNumber(entry.sales)
        const miscCosts = toNumber(entry.miscCostPerDay)
        const omsSpend = pickGpOmsSpend(vatPreference, entry)
        const omsSpendExVat = toNumber(entry.purchasesOnOMS)
        const spend = miscCosts + omsSpend
        // Profit stays on the ex-VAT basis so it matches the server's GP%.
        const profitPercent =
          revenue > 0
            ? ((revenue - (miscCosts + omsSpendExVat)) / revenue) * 100
            : 0
        return {
          dateISO: date.format('YYYY-MM-DD'),
          displayDate: date.format('ddd DD MMM'),
          revenue,
          spend,
          profitPercent,
        }
      })
  }, [previousYearData, vatPreference])

  const currencyMaxValue = useMemo(() => {
    return analyticsData.reduce(
      (max, point) => Math.max(max, point.revenue, point.spend),
      0,
    )
  }, [analyticsData])

  const maxProfitPercent = useMemo(() => {
    return analyticsData.reduce(
      (max, point) => Math.max(max, point.profitPercent),
      0,
    )
  }, [analyticsData])

  const profitScaleBase = useMemo(() => {
    if (currencyMaxValue > 0) return currencyMaxValue
    if (maxProfitPercent > 0) return maxProfitPercent
    return 100
  }, [currencyMaxValue, maxProfitPercent])

  const weekAverageData = useMemo(() => {
    if (!analyticsData.length) return []
    return analyticsData.map((point, index) => {
      const windowStart = Math.max(0, index - 6)
      const windowPoints = analyticsData
        .slice(windowStart, index + 1)
        .filter(p => !p.isFuture)
      const averageRevenue =
        windowPoints.length > 0
          ? windowPoints.reduce((sum, item) => sum + item.revenue, 0) /
            windowPoints.length
          : null
      const averageSpend =
        windowPoints.length > 0
          ? windowPoints.reduce((sum, item) => sum + item.spend, 0) /
            windowPoints.length
          : null
      const averageProfitPercent =
        windowPoints.length > 0
          ? windowPoints.reduce((sum, item) => sum + item.profitPercent, 0) /
            windowPoints.length
          : null
      return {
        ...point,
        averageRevenue,
        averageSpend,
        averageProfitPercent,
      }
    })
  }, [analyticsData])

  const alignedPreviousYearData = useMemo(() => {
    if (!previousYearAnalyticsData.length || !analyticsData.length) return []
    const currentDates = analyticsData.map(point => dayjs(point.dateISO))
    const previousYearMap = new Map(
      previousYearAnalyticsData.map(point => [
        dayjs(point.dateISO).format('YYYY-MM-DD'),
        point,
      ]),
    )
    return currentDates.map(date => {
      const previousYearDate = date.subtract(1, 'year').format('YYYY-MM-DD')
      const previousYearEntry = previousYearMap.get(previousYearDate)
      return {
        dateISO: date.format('YYYY-MM-DD'),
        displayDate: date.format('ddd DD MMM'),
        revenue: previousYearEntry?.revenue ?? 0,
        spend: previousYearEntry?.spend ?? 0,
        profitPercent: previousYearEntry?.profitPercent ?? 0,
      }
    })
  }, [analyticsData, previousYearAnalyticsData])

  const metricDefinitions = useMemo(
    () => ({
      revenue: {
        key: 'revenue',
        label: 'Revenue',
        color: revenueColour,
        value: point => point.revenue,
        raw: point => point.revenue,
        format: formatCurrency,
      },
      spend: {
        key: 'spend',
        label: 'Spend',
        color: spendColour,
        value: point => point.spend,
        raw: point => point.spend,
        format: formatCurrency,
      },
      profit: {
        key: 'profit',
        label: 'Profit %',
        color: profitColour,
        value: point =>
          profitScaleBase > 0
            ? (point.profitPercent / 100) * profitScaleBase
            : 0,
        raw: point => point.profitPercent,
        format: formatPercent,
        axisFormatter: value =>
          formatPercent((value / (profitScaleBase || 1)) * 100),
      },
    }),
    [profitColour, profitScaleBase, revenueColour, spendColour],
  )

  const selectedDefinitions = useMemo(
    () =>
      selectedMetrics.map(metric => metricDefinitions[metric]).filter(Boolean),
    [metricDefinitions, selectedMetrics],
  )

  const baseSeries = useMemo(
    () =>
      selectedDefinitions.map(def => ({
        id: def.label,
        data: analyticsData.map(point => {
          const value = def.value(point)
          const rawValue = def.raw(point)
          return {
            x: point.dateISO,
            y: value,
            rawValue,
            formattedValue: def.format(rawValue),
            displayDate: point.displayDate,
            isFuture: point.isFuture,
          }
        }),
      })),
    [analyticsData, selectedDefinitions],
  )

  const overlaySeries = useMemo(() => {
    const series = []
    if (weekAverageEnabled && weekAverageData.length) {
      if (selectedMetrics.includes('revenue')) {
        const weekRevenuePoints = weekAverageData.filter(
          point => point.averageRevenue != null,
        )
        if (weekRevenuePoints.length) {
          series.push({
            id: 'Week avg (Revenue)',
            color: hexToRgba(revenueColour, 0.6),
            styleVariant: 'weekAvg',
            data: weekRevenuePoints.map(point => ({
              x: point.dateISO,
              y: point.averageRevenue,
              rawValue: point.averageRevenue,
              formattedValue: formatCurrency(point.averageRevenue),
              displayDate: point.displayDate,
              overlayType: 'weekAvg',
            })),
          })
        }
      }
      if (selectedMetrics.includes('spend')) {
        const weekSpendPoints = weekAverageData.filter(
          point => point.averageSpend != null,
        )
        if (weekSpendPoints.length) {
          series.push({
            id: 'Week avg (Spend)',
            color: hexToRgba(spendColour, 0.6),
            styleVariant: 'weekAvg',
            data: weekSpendPoints.map(point => ({
              x: point.dateISO,
              y: point.averageSpend,
              rawValue: point.averageSpend,
              formattedValue: formatCurrency(point.averageSpend),
              displayDate: point.displayDate,
              overlayType: 'weekAvg',
            })),
          })
        }
      }
      if (selectedMetrics.includes('profit')) {
        const weekProfitPoints = weekAverageData.filter(
          point => point.averageProfitPercent != null,
        )
        if (weekProfitPoints.length) {
          series.push({
            id: 'Week avg (Profit %)',
            color: hexToRgba(profitColour, 0.6),
            styleVariant: 'weekAvg',
            data: weekProfitPoints.map(point => ({
              x: point.dateISO,
              y:
                profitScaleBase > 0
                  ? (point.averageProfitPercent / 100) * profitScaleBase
                  : 0,
              rawValue: point.averageProfitPercent,
              formattedValue: formatPercent(point.averageProfitPercent),
              displayDate: point.displayDate,
              overlayType: 'weekAvg',
            })),
          })
        }
      }
    }
    if (
      previousYearEnabled &&
      alignedPreviousYearData.length &&
      !previousYearStatus.loading
    ) {
      if (selectedMetrics.includes('revenue')) {
        series.push({
          id: 'Prev year (Revenue)',
          color: hexToRgba(revenueColour, 0.4),
          styleVariant: 'prevYear',
          data: alignedPreviousYearData.map(point => ({
            x: point.dateISO,
            y: point.revenue,
            rawValue: point.revenue,
            formattedValue: formatCurrency(point.revenue),
            displayDate: point.displayDate,
            overlayType: 'prevYear',
          })),
        })
      }
      if (selectedMetrics.includes('spend')) {
        series.push({
          id: 'Prev year (Spend)',
          color: hexToRgba(spendColour, 0.4),
          styleVariant: 'prevYear',
          data: alignedPreviousYearData.map(point => ({
            x: point.dateISO,
            y: point.spend,
            rawValue: point.spend,
            formattedValue: formatCurrency(point.spend),
            displayDate: point.displayDate,
            overlayType: 'prevYear',
          })),
        })
      }
      if (selectedMetrics.includes('profit')) {
        series.push({
          id: 'Prev year (Profit %)',
          color: hexToRgba(profitColour, 0.4),
          styleVariant: 'prevYear',
          data: alignedPreviousYearData.map(point => ({
            x: point.dateISO,
            y:
              profitScaleBase > 0
                ? (point.profitPercent / 100) * profitScaleBase
                : 0,
            rawValue: point.profitPercent,
            formattedValue: formatPercent(point.profitPercent),
            displayDate: point.displayDate,
            overlayType: 'prevYear',
          })),
        })
      }
    }
    return series
  }, [
    weekAverageEnabled,
    weekAverageData,
    selectedMetrics,
    revenueColour,
    spendColour,
    previousYearEnabled,
    alignedPreviousYearData,
    previousYearStatus.loading,
    profitScaleBase,
    profitColour,
  ])

  const combinedSeries = useMemo(
    () => [...baseSeries, ...overlaySeries],
    [baseSeries, overlaySeries],
  )

  const overlaySeriesIds = useMemo(
    () => new Set(overlaySeries.map(series => series.id)),
    [overlaySeries],
  )

  const legendEntries = useMemo(() => {
    const baseEntries = selectedDefinitions.map(def => ({
      key: def.key,
      label: def.label,
      color: def.color,
      variant: 'solid',
      showSpendNote: def.key === 'spend',
    }))
    const overlayEntries = overlaySeries.map(series => ({
      key: series.id,
      label: series.id,
      color: series.color,
      variant: series.styleVariant ?? 'overlay',
      showSpendNote: false,
    }))
    return [...baseEntries, ...overlayEntries]
  }, [overlaySeries, selectedDefinitions])

  const hasChartData = useMemo(
    () => combinedSeries.some(series => series.data.length > 0),
    [combinedSeries],
  )

  const onlyProfitSelected =
    selectedDefinitions.length === 1 && selectedDefinitions[0]?.key === 'profit'

  const colorBySerieId = useMemo(() => {
    const map = {}
    selectedDefinitions.forEach(def => {
      map[def.label] = def.color
    })
    overlaySeries.forEach(series => {
      map[series.id] = series.color
    })
    return map
  }, [selectedDefinitions, overlaySeries])

  const metricDefinitionByLabel = useMemo(() => {
    const map = {}
    Object.values(metricDefinitions).forEach(def => {
      map[def.label] = def
    })
    overlaySeries.forEach(series => {
      map[series.id] = {
        label: series.id,
        format: series.id.includes('Profit') ? formatPercent : formatCurrency,
        color: series.color,
      }
    })
    return map
  }, [metricDefinitions, overlaySeries])

  const axisLeftConfig = useMemo(() => {
    if (onlyProfitSelected) {
      const formatter = metricDefinitions.profit?.axisFormatter ?? formatPercent
      return {
        tickSize: 0,
        tickPadding: 8,
        format: value => formatter(value),
      }
    }
    return {
      tickSize: 0,
      tickPadding: 8,
      format: value => formatAxisCurrency(value),
    }
  }, [metricDefinitions, onlyProfitSelected])

  const axisRightConfig = useMemo(() => {
    if (onlyProfitSelected || !selectedMetrics.includes('profit')) {
      return null
    }
    const formatter =
      metricDefinitions.profit?.axisFormatter ??
      (value => formatPercent((value / (profitScaleBase || 1)) * 100))
    return {
      tickSize: 0,
      tickPadding: 8,
      format: value => formatter(value),
    }
  }, [metricDefinitions, onlyProfitSelected, profitScaleBase, selectedMetrics])

  const chartTheme = useMemo(
    () => ({
      axis: {
        ticks: {
          text: {
            fill: '#6B6B6B',
            fontSize: 12,
          },
        },
        domain: {
          line: {
            stroke: '#E5E5E5',
            strokeWidth: 1,
          },
        },
      },
      grid: {
        line: {
          stroke: '#F3F3F3',
          strokeWidth: 1,
        },
      },
      tooltip: {
        container: {
          fontSize: 12,
          borderRadius: 8,
        },
      },
    }),
    [],
  )

  const onChangeDate = (dates, dateStrings) => {
    setVisibleCalendar(false)
    if (dateStrings && dateStrings[0] && dateStrings[1]) {
      dispatch(
        setDateRange({
          startDate: dateStrings[0],
          endDate: dateStrings[1],
        }),
      )
    }
  }
  const disableEndDate = current => current.valueOf() > moment.now()

  const handleQuickRangeSelect = value => {
    if (value === 'custom') {
      setActiveQuickRange('custom')
      setVisibleCalendar(true)
      return
    }

    const today = dayjs().startOf('day')
    let start

    switch (value) {
      case '7d':
        start = today.subtract(6, 'day')
        break
      case '30d':
        start = today.subtract(29, 'day')
        break
      case '90d':
        start = today.subtract(89, 'day')
        break
      case 'this-month':
        start = today.startOf('month')
        break
      case '12m':
        start = today.subtract(11, 'month').startOf('month')
        break
      default:
        start = null
        break
    }

    if (!start) {
      setActiveQuickRange('custom')
      return
    }

    const newRange = {
      startDate: start.format('YYYY-MM-DD'),
      endDate: today.format('YYYY-MM-DD'),
    }

    setActiveQuickRange(value)
    dispatch(setDateRange(newRange))
  }

  const handleMetricChange = values => {
    if (values.length === 0) {
      return
    }
    setSelectedMetrics(values)
  }

  const handleXLSXExport = async () => {
    if (!totalSaleWeek || !dateRange) return

    let splitRows = []
    if (
      venueId &&
      activeRevenueCategories.length > 0 &&
      currentPlan !== 'Free'
    ) {
      try {
        const data = await readRevenueDays(
          venueId,
          dateRange.startDate,
          dateRange.endDate,
        )
        const loaded = revenueSplitExportRows(data?.days || [])
        if (loaded.some(row => row.category !== 'Recorded as one total')) {
          splitRows = loaded
        }
      } catch (_error) {
        splitRows = []
      }
    }

    // Helper function to round to exactly 2 decimal places
    const roundTo2Decimals = value => {
      return Math.round((value || 0) * 100) / 100
    }

    // Calculate summary metrics
    const daysCount = totalSaleWeek.length || 1
    const avgRevenue = roundTo2Decimals(
      daysCount > 0 ? totalSaleAmount / daysCount : 0,
    )
    const totalForecasted = totalSaleWeek.reduce(
      (sum, day) => sum + (day.forecastedSales || 0),
      0,
    )
    const avgForecasted = roundTo2Decimals(
      daysCount > 0 ? totalForecasted / daysCount : 0,
    )
    const totalOmsSpend = totalSaleWeek.reduce(
      (sum, day) => sum + pickGpOmsSpend(vatPreference, day),
      0,
    )
    const avgOmsSpend = roundTo2Decimals(
      daysCount > 0 ? totalOmsSpend / daysCount : 0,
    )
    const avgMisc = roundTo2Decimals(
      daysCount > 0 ? totalMiscCosts / daysCount : 0,
    )
    const totalSpend = totalSaleWeek.reduce(
      (sum, day) =>
        sum + pickGpOmsSpend(vatPreference, day) + (day.miscCostPerDay || 0),
      0,
    )
    const avgTotalSpend = roundTo2Decimals(
      daysCount > 0 ? totalSpend / daysCount : 0,
    )

    // Calculate percentages for color coding
    const gpValue = parseFloat(GP || '0')
    const gpExclMiscValue = parseFloat(GPExclMisc || '0')
    const miscCostsPercent =
      totalSaleAmount > 0
        ? ((totalMiscCosts / totalSaleAmount) * 100).toFixed(1)
        : '0.0'
    const miscCostsPercentValue = parseFloat(miscCostsPercent)
    const omsSpendPercent =
      totalSaleAmount > 0
        ? ((totalOmsSpend / totalSaleAmount) * 100).toFixed(1)
        : '0.0'
    const omsSpendPercentValue = parseFloat(omsSpendPercent)

    // Build worksheet data array
    const worksheetData = []

    // Summary metrics section (values already rounded to 2 decimal places)
    worksheetData.push(['Summary Metrics'])
    worksheetData.push(['Avg. Revenue / Day', avgRevenue])
    worksheetData.push(['Avg. Forecasted Revenue / Day', avgForecasted])
    worksheetData.push([
      `Avg. OMS Spend / Day ${vatLabelSuffix(vatPreference)}`,
      avgOmsSpend,
    ])
    worksheetData.push(['Avg. Misc Costs / Day', avgMisc])
    worksheetData.push(['Avg. Total Spend / Day', avgTotalSpend])
    worksheetData.push(['GP %', GP || '0.0'])
    worksheetData.push(['GP % (Excl. Misc Costs)', GPExclMisc || '0.0'])
    worksheetData.push(['Misc Costs %', miscCostsPercent])
    worksheetData.push(['OMS Spend %', omsSpendPercent])
    const excludedSummary =
      formatGpExcludedThisPeriodSummary(excludedThisPeriod)
    worksheetData.push(['Excluded this period', excludedSummary || 'None'])
    worksheetData.push([
      'Total Forecasted Revenue',
      roundTo2Decimals(totalForecasted),
    ])
    worksheetData.push(['Total Revenue', roundTo2Decimals(totalSaleAmount)])
    worksheetData.push([]) // Blank row

    // Table headers
    worksheetData.push([
      'Date',
      'Day',
      'Revenue',
      'Misc Costs',
      `OMS Spend ${vatLabelSuffix(vatPreference)}`,
      'Forecasted Revenue',
      'Variance',
      'Notes',
    ])

    // Table data rows
    totalSaleWeek.forEach(record => {
      const variance = (record.sales || 0) - (record.forecastedSales || 0)
      worksheetData.push([
        record.date || '',
        record.day || '',
        record.sales || 0,
        record.miscCostPerDay || 0,
        pickGpOmsSpend(vatPreference, record),
        record.forecastedSales || 0,
        variance,
        record.notes || '',
      ])
    })

    if (splitRows.length) {
      worksheetData.push([])
      worksheetData.push(['Sales split'])
      worksheetData.push([
        'Date',
        'Category',
        'Entry',
        'Line total',
        'Period total',
        'Share of revenue',
      ])
      splitRows.forEach(row => {
        worksheetData.push([
          row.date,
          row.category,
          row.entry,
          roundTo2Decimals(row.lineTotal),
          roundTo2Decimals(row.periodTotal),
          `${(row.share * 100).toFixed(1)}%`,
        ])
      })
    }

    // Create worksheet from data
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

    // Apply number formatting to summary metric cells (2 decimal places for currency values)
    // Row indices: 1=Avg Revenue, 2=Avg Forecasted, 3=Avg OMS Spend, 4=Avg Misc, 5=Avg Total Spend, 11=Total Forecasted, 12=Total Revenue
    const summaryCurrencyCells = [1, 2, 3, 4, 5, 11, 12] // Row indices for cells that need 2 decimal places
    summaryCurrencyCells.forEach(rowIndex => {
      const cell = worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: 1 })]
      if (cell) {
        if (!cell.s) cell.s = {}
        cell.s.numFmt = '0.00' // Format to 2 decimal places
      }
    })

    // Apply colors to summary metrics (row indices are 0-based, accounting for header row)
    // Using background fill colors for better compatibility with Numbers and Excel
    // Row 1: GP % (index 6)
    const gpCell = worksheet[XLSX.utils.encode_cell({ r: 6, c: 1 })]
    if (gpCell) {
      gpCell.s = {
        ...gpCell.s, // Preserve existing formatting (like numFmt)
        fill: {
          fgColor: { rgb: gpValue >= 75 ? 'C6E6C6' : 'FFE6E6' }, // Light green/red background
        },
        font: { color: { rgb: gpValue >= 75 ? '2D5016' : '8B0000' } }, // Dark text for readability
      }
    }

    // Row 2: GP % (Excl. Misc Costs) (index 7)
    const gpExclMiscCell = worksheet[XLSX.utils.encode_cell({ r: 7, c: 1 })]
    if (gpExclMiscCell) {
      gpExclMiscCell.s = {
        ...gpExclMiscCell.s, // Preserve existing formatting
        fill: {
          fgColor: { rgb: gpExclMiscValue >= 75 ? 'C6E6C6' : 'FFE6E6' },
        },
        font: { color: { rgb: gpExclMiscValue >= 75 ? '2D5016' : '8B0000' } },
      }
    }

    // Row 3: Misc Costs % (index 8)
    const miscCostsCell = worksheet[XLSX.utils.encode_cell({ r: 8, c: 1 })]
    if (miscCostsCell) {
      miscCostsCell.s = {
        ...miscCostsCell.s, // Preserve existing formatting
        fill: {
          fgColor: { rgb: miscCostsPercentValue >= 45 ? 'FFE6E6' : 'C6E6C6' },
        },
        font: {
          color: { rgb: miscCostsPercentValue >= 45 ? '8B0000' : '2D5016' },
        },
      }
    }

    // Row 4: OMS Spend % (index 9)
    const omsSpendCell = worksheet[XLSX.utils.encode_cell({ r: 9, c: 1 })]
    if (omsSpendCell) {
      omsSpendCell.s = {
        ...omsSpendCell.s, // Preserve existing formatting
        fill: {
          fgColor: { rgb: omsSpendPercentValue >= 30 ? 'FFE6E6' : 'C6E6C6' },
        },
        font: {
          color: { rgb: omsSpendPercentValue >= 30 ? '8B0000' : '2D5016' },
        },
      }
    }

    // Row 7: Total Revenue (index 12)
    const totalRevenueCell = worksheet[XLSX.utils.encode_cell({ r: 12, c: 1 })]
    if (totalRevenueCell) {
      totalRevenueCell.s = {
        ...totalRevenueCell.s, // Preserve existing formatting
        fill: {
          fgColor: {
            rgb: totalSaleAmount >= totalForecasted ? 'C6E6C6' : 'FFE6E6',
          },
        },
        font: {
          color: {
            rgb: totalSaleAmount >= totalForecasted ? '2D5016' : '8B0000',
          },
        },
      }
    }

    // Apply colors to variance column in daily table rows
    // Variance is column G (index 6), starting from row 14 (index 13, after header)
    const headerRowIndex = 13 // Row index where table headers start
    totalSaleWeek.forEach((record, index) => {
      const rowIndex = headerRowIndex + 1 + index // +1 because header is row 13
      const variance = (record.sales || 0) - (record.forecastedSales || 0)
      const varianceCell =
        worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: 6 })]
      if (varianceCell) {
        varianceCell.s = {
          fill: {
            fgColor: { rgb: variance >= 0 ? 'C6E6C6' : 'FFE6E6' },
          },
          font: { color: { rgb: variance >= 0 ? '2D5016' : '8B0000' } },
        }
      }
    })

    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Gross Profit Report')

    // Generate filename with date range
    const startDate = dateRange.startDate.replace(/-/g, '')
    const endDate = dateRange.endDate.replace(/-/g, '')
    const filename = `gross-profit-${startDate}-to-${endDate}.xlsx`

    // Write workbook to file and download
    XLSX.writeFile(workbook, filename)
  }

  // Sync local state when venue loads/changes (using stable venue ID as dependency)
  useEffect(() => {
    if (venue?._id) {
      const emails = venue?.gpReportEmails || []
      setGpReportEmails(emails)
      setGpReportFrequency(venue?.gpReportFrequency || 'none')
    }
  }, [venue?._id]) // Use stable venue ID instead of array reference

  // Save handler for email report settings (emails + frequency)
  const handleSaveEmailSettings = () => {
    if (venueId) {
      dispatch(
        editVenue(
          {
            gpReportEmails,
            gpReportFrequency,
          },
          venueId,
        ),
      )
      // Show saved indicator
      setShowSaved(true)
      setTimeout(() => setShowSaved(false), 2000)
    }
  }

  useEffect(() => {
    if (venueId) {
      const requested = grossProfitRangeFromSearch(salesSearch)
      if (requested) setActiveQuickRange('custom')
      dispatch(
        setDateRange(
          requested || {
            startDate: defaultDateRange.start,
            endDate: defaultDateRange.end,
          },
        ),
      )
    }
  }, [venueId, salesSearch])

  useEffect(() => {
    if (dateRange) {
      setPreviousYearData(null)
      setPreviousYearStatus({ loading: false, error: null })
      dispatch(
        fetchGrossProfitAndSales(
          dateRange.startDate,
          dateRange.endDate,
          venueId,
        ),
      )
    }
  }, [dateRange])

  useEffect(() => {
    if (
      !previousYearEnabled ||
      !dateRange?.startDate ||
      !dateRange?.endDate ||
      !venueId
    ) {
      return
    }
    let cancelled = false
    const fetchPreviousYear = async () => {
      try {
        setPreviousYearStatus({ loading: true, error: null })
        const prevStart = dayjs(dateRange.startDate)
          .subtract(1, 'year')
          .format('YYYY-MM-DD')
        const prevEnd = dayjs(dateRange.endDate)
          .subtract(1, 'year')
          .format('YYYY-MM-DD')
        const response = await API.get(
          'api',
          `gross-profit/sales-per-day?fromDate=${prevStart}&toDate=${prevEnd}&venue=${venueId}`,
        )
        if (cancelled) return
        setPreviousYearData(response)
        setPreviousYearStatus({ loading: false, error: null })
      } catch (error) {
        if (cancelled) return
        console.error('Failed to fetch previous year data', error)
        setPreviousYearData(null)
        setPreviousYearStatus({
          loading: false,
          error:
            'Unable to load previous year data right now. Please try again later.',
        })
      }
    }
    fetchPreviousYear()
    return () => {
      cancelled = true
    }
  }, [previousYearEnabled, dateRange, venueId])

  useEffect(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      setActiveQuickRange('custom')
      return
    }

    const start = dayjs(dateRange.startDate)
    const end = dayjs(dateRange.endDate)
    const today = dayjs().startOf('day')
    const diff = end.diff(start, 'day')

    let preset = 'custom'
    if (end.isSame(today, 'day')) {
      if (diff === 6) preset = '7d'
      else if (diff === 29) preset = '30d'
      else if (diff === 89) preset = '90d'
      else if (
        start.isSame(today.startOf('month'), 'day') &&
        diff >= today.date() - 1
      ) {
        preset = 'this-month'
      } else if (diff >= 334 && diff <= 367) {
        preset = '12m'
      }
    }
    setActiveQuickRange(preset)
  }, [dateRange])

  const rangePickerValue = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return []
    return [moment(dateRange.startDate), moment(dateRange.endDate)]
  }, [dateRange])

  const renderPurchases = record => {
    const purchases = Array.isArray(record?.purchases) ? record.purchases : []
    const dataSource = purchases.map((purchase, index) => ({
      key: `purchase-${index}`,
      ...purchase,
      purchaseIndex: index,
    }))

    const handleDeletePurchase = purchaseIndex => {
      if (!dateRange || !record?.date) return
      const updatedPurchases = purchases.filter(
        (_, index) => index !== purchaseIndex,
      )
      const body = {
        date: dayjs(record.date).format('YYYY-MM-DD'),
        purchase: updatedPurchases,
      }
      dispatch(sendSalesOrPurchaseData(venueId, body, dateRange))
    }

    return (
      <div style={{ marginLeft: 20 }}>
        <DayRevenueLines
          lines={
            expandedRevenue[dayjs(record.date).format('YYYY-MM-DD')]?.lines
          }
          sales={record?.sales}
          loading={
            expandedRevenue[dayjs(record.date).format('YYYY-MM-DD')]?.loading
          }
        />
        {dataSource.length ? (
          <Table
            dataSource={dataSource}
            pagination={false}
            style={{ background: 'white' }}
            columns={[
              {
                title: '',
                dataIndex: '',
                key: '',
              },
              {
                title: '',
                dataIndex: '',
                key: '',
              },
              {
                title: 'Wages/Utility',
                dataIndex: 'text',
                key: 'text',
                className: 'restrictShrink',
                sorter: (a, b) =>
                  String(a.product)
                    .trim()
                    .localeCompare(String(b.product).trim()),
                sortDirections: ['ascend', 'descend'],
              },
              {
                title: 'Purchase figure',
                dataIndex: 'amount',
                key: 'amount',
                className: 'restrictShrink',
                sorter: (a, b) =>
                  String(a.product)
                    .trim()
                    .localeCompare(String(b.product).trim()),
                sortDirections: ['ascend', 'descend'],
                render: text => formatCurrency(text),
              },
              {
                title: '',
                key: 'actions',
                render: (_, purchaseRecord) => (
                  <Popconfirm
                    title='Remove this misc cost entry?'
                    okText='Delete'
                    okType='danger'
                    cancelText='Cancel'
                    onConfirm={() =>
                      handleDeletePurchase(purchaseRecord.purchaseIndex)
                    }
                  >
                    <Button type='link' danger size='small'>
                      Delete
                    </Button>
                  </Popconfirm>
                ),
              },
            ]}
          />
        ) : null}
      </div>
    )
  }

  const startInlineEdit = (record, field) => {
    if (field === 'sales' && activeRevenueCategories.length > 0) {
      startCategoryRevenueEdit(record)
      return
    }
    setCategoryEdit(null)
    const currentValue = record?.[field]
    setInlineEditCell({ date: record?.date, field, record })
    setInlineEditValue(formatNumberForInlineInput(currentValue))
    setInlineEditError('')
  }

  const startCategoryRevenueEdit = async record => {
    if (currentPlan === 'Free') {
      setShowLimitModal(true)
      return
    }
    const date = dayjs(record.date).format('YYYY-MM-DD')
    cancelInlineEdit()
    setCategoryEdit({
      date,
      entries: {},
      loadedLines: [],
      savedSale: null,
      loading: true,
      saving: false,
      error: '',
    })
    try {
      const day = await readRevenueDay(venueId, date)
      const lines = Array.isArray(day?.revenueLines) ? day.revenueLines : []
      const entries = {}
      lines.forEach(line => {
        entries[String(line.categoryId)] =
          line.entry == null ? '' : String(line.entry)
      })
      const savedSale =
        day?.sale != null && Number(day.sale) !== 0 ? Number(day.sale) : null
      setCategoryEdit({
        date,
        entries,
        loadedLines: lines,
        savedSale,
        loading: false,
        saving: false,
        error: '',
      })
    } catch (error) {
      setCategoryEdit(current =>
        current?.date === date
          ? {
              ...current,
              loading: false,
              error: 'That day could not be opened.',
            }
          : current,
      )
    }
  }

  const categoryEditRows = () => {
    if (!categoryEdit) return []
    const savedByCategory = {}
    ;(categoryEdit.loadedLines || []).forEach(line => {
      savedByCategory[String(line.categoryId)] = line
    })
    return activeRevenueCategories.map(category => {
      const saved = savedByCategory[String(category._id)]
      const entry = categoryEdit.entries[category._id]
      const unchanged =
        saved && String(entry ?? '') === String(saved.entry ?? '')
      const preview = previewRevenueLine({
        entry,
        multiplier: unchanged ? saved.multiplier : category.multiplier,
      })
      return {
        ...category,
        multiplier: unchanged ? saved.multiplier : category.multiplier,
        mode: unchanged
          ? saved.mode
          : Number(category.multiplier) === 1
          ? 'amount'
          : 'quantity',
        preview,
      }
    })
  }

  const writeCategoryRevenue = async () => {
    if (!categoryEdit || categoryEdit.saving) return
    const rows = categoryEditRows()
    if (rows.some(row => row.preview.error)) {
      setCategoryEdit(current => ({
        ...current,
        error: 'Enter a number for each line.',
      }))
      return
    }
    const payload = {
      date: categoryEdit.date,
      entries: activeRevenueCategories.map(category => ({
        categoryId: category._id,
        entry: categoryEdit.entries[category._id] ?? '',
      })),
    }
    const send = async () => {
      setCategoryEdit(current => ({ ...current, saving: true, error: '' }))
      try {
        await saveRevenueDay(venueId, payload)
        await dispatch(
          fetchGrossProfitAndSales(
            dateRange.startDate,
            dateRange.endDate,
            venueId,
            { silent: true },
          ),
        )
        setCategoryEdit(null)
      } catch (error) {
        const apiErrors = error?.response?.data?.errors
        setCategoryEdit(current =>
          current
            ? {
                ...current,
                saving: false,
                error:
                  (Array.isArray(apiErrors) && apiErrors[0]) ||
                  'That day could not be saved.',
              }
            : current,
        )
      }
    }
    const replacedFigure = saleFigureLinesWouldReplace(
      categoryEdit.savedSale,
      previewRevenueTotal(rows.map(row => row.preview)),
    )
    if (replacedFigure) {
      Modal.confirm({
        title: 'Replace the existing sales figure?',
        content: `${dayjs(categoryEdit.date).format(
          'dddd D MMMM',
        )} is saved as ${formatCurrency(
          replacedFigure.saved,
        )}. These lines total ${formatCurrency(
          replacedFigure.next,
        )}. Saving replaces the saved figure.`,
        okText: 'Replace figure',
        cancelText: 'Keep the existing figure',
        onOk: send,
      })
      return
    }
    await send()
  }

  const cancelInlineEdit = () => {
    setInlineEditCell(null)
    setInlineEditValue('')
    setInlineEditError('')
    inlineCommitRef.current = false
  }

  const normalizeInlineNumericValue = raw => {
    if (raw === null || raw === undefined) return 0
    const text = String(raw).trim()
    if (text === '') return 0
    const parsed = Number(text)
    return Number.isFinite(parsed) ? parsed : null
  }

  const persistInlineCellChange = async ({ record, field, value }) => {
    if (currentPlan === 'Free') {
      setShowLimitModal(true)
      return false
    }
    if (!venueId || !dateRange?.startDate || !dateRange?.endDate) {
      setInlineEditError('Missing venue or date range.')
      return false
    }

    const dateStr = dayjs(record.date).format('YYYY-MM-DD')
    const previous = toNumber(record[field])
    if (previous === value) {
      return true
    }

    let body
    if (field === 'sales') {
      body = { date: dateStr, sale: value, isSale: true }
    } else if (field === 'miscCostPerDay') {
      body = {
        date: dateStr,
        purchase: [{ text: 'Misc costs', amount: value }],
      }
    } else if (field === 'forecastedSales') {
      body = { date: dateStr, forecastedSales: value }
    } else {
      return false
    }

    await dispatch(
      sendSalesOrPurchaseData(venueId, body, dateRange, { silent: true }),
    )
    return true
  }

  const commitInlineEdit = async () => {
    if (!inlineEditCell || inlineCommitRef.current) return

    const normalizedValue = normalizeInlineNumericValue(inlineEditValue)
    if (normalizedValue === null) {
      setInlineEditError('Please enter a valid number.')
      return
    }

    inlineCommitRef.current = true
    setInlineEditError('')
    try {
      const saved = await persistInlineCellChange({
        record: inlineEditCell.record,
        field: inlineEditCell.field,
        value: normalizedValue,
      })
      if (saved === false) return
      cancelInlineEdit()
    } catch (error) {
      console.error('Failed to save inline edit', error)
      setInlineEditError('Unable to save right now. Please try again.')
    } finally {
      inlineCommitRef.current = false
    }
  }

  const onInlineInputKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitInlineEdit()
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      cancelInlineEdit()
    }
  }

  const renderCategoryRevenueCell = (record, value) => {
    const date = dayjs(record.date).format('YYYY-MM-DD')
    const isActive = categoryEdit?.date === date
    if (!isActive) {
      const lines = revenueLinesByDate ? revenueLinesByDate[date] || [] : null
      const gap = lines ? uncategorisedRevenue(value, lines) : null
      const oneTotal = lines ? isRecordedAsOneTotal(value, lines) : false
      return (
        <div>
          <Tooltip
            title='Edit each revenue category. The day total updates from those lines.'
            mouseEnterDelay={0.4}
          >
            <span
              role='button'
              tabIndex={0}
              className='gp-inline-edit-trigger'
              onClick={() => startCategoryRevenueEdit(record)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  startCategoryRevenueEdit(record)
                }
              }}
            >
              <span className='gp-inline-edit-value'>
                {formatCurrency(value)}
              </span>
              <EditOutlined className='gp-inline-edit-icon' aria-hidden />
            </span>
          </Tooltip>
          {gap == null ? null : (
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
              {`${formatCurrency(gap)} not in a category`}
            </div>
          )}
          {oneTotal ? (
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
              One total
            </div>
          ) : null}
        </div>
      )
    }

    if (categoryEdit.loading) {
      return <span>Loading categories…</span>
    }

    const rows = categoryEditRows()
    const total = previewRevenueTotal(rows.map(row => row.preview))
    return (
      <div className='gp-inline-edit-cell' style={{ minWidth: 220 }}>
        {rows.map(row => (
          <div key={row._id} style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)' }}>
              {row.name}{' '}
              {row.mode === 'amount'
                ? '(Amount)'
                : `(Quantity × ${Number(row.multiplier).toFixed(2)})`}
            </div>
            <Input
              size='small'
              inputMode='decimal'
              aria-label={
                row.mode === 'amount'
                  ? `${row.name} amount`
                  : `${row.name} quantity`
              }
              value={categoryEdit.entries[row._id] || ''}
              onChange={event =>
                setCategoryEdit(current =>
                  current
                    ? {
                        ...current,
                        entries: {
                          ...current.entries,
                          [row._id]: event.target.value,
                        },
                      }
                    : current,
                )
              }
            />
            {row.preview.error ? (
              <div className='gp-inline-edit-error' role='alert'>
                {row.preview.error}
              </div>
            ) : null}
          </div>
        ))}
        <div style={{ fontSize: 12, marginBottom: 6 }}>
          {!(categoryEdit.loadedLines || []).length &&
          categoryEdit.savedSale != null &&
          total == null
            ? `One total ${formatCurrency(categoryEdit.savedSale)}`
            : `Day total ${total == null ? '—' : formatCurrency(total)}`}
        </div>
        {!(categoryEdit.loadedLines || []).length &&
        categoryEdit.savedSale != null &&
        total != null ? (
          <div
            style={{ fontSize: 12, marginBottom: 6, color: 'rgba(0,0,0,0.65)' }}
          >
            {`Saved as one total ${formatCurrency(
              categoryEdit.savedSale,
            )}. Saving these lines replaces it.`}
          </div>
        ) : null}
        {categoryEdit.error ? (
          <div className='gp-inline-edit-error' role='alert'>
            {categoryEdit.error}
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type='primary'
            size='small'
            loading={categoryEdit.saving}
            onClick={writeCategoryRevenue}
          >
            Save day
          </Button>
          <Button size='small' onClick={() => setCategoryEdit(null)}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  const renderInlineEditableCurrencyCell = (record, field, value) => {
    const isActive =
      inlineEditCell?.date === record?.date && inlineEditCell?.field === field

    if (isActive) {
      return (
        <div className='gp-inline-edit-cell'>
          <Input
            autoFocus
            size='small'
            className='gp-inline-edit-input'
            inputMode='decimal'
            autoComplete='off'
            placeholder='0 (empty = £0)'
            aria-label='Edit value, Enter to save, Escape to cancel'
            value={inlineEditValue}
            onChange={event => setInlineEditValue(event.target.value)}
            onPressEnter={commitInlineEdit}
            onBlur={commitInlineEdit}
            onKeyDown={onInlineInputKeyDown}
          />
          {inlineEditError && (
            <div className='gp-inline-edit-error' role='alert'>
              {inlineEditError}
            </div>
          )}
        </div>
      )
    }

    return (
      <Tooltip
        title='Click to edit. Enter to save, Esc to cancel. Empty saves as £0. Decimals allowed.'
        mouseEnterDelay={0.4}
      >
        <span
          role='button'
          tabIndex={0}
          className='gp-inline-edit-trigger'
          onClick={() => startInlineEdit(record, field)}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              startInlineEdit(record, field)
            }
          }}
        >
          <span className='gp-inline-edit-value'>{formatCurrency(value)}</span>
          <EditOutlined className='gp-inline-edit-icon' aria-hidden />
        </span>
      </Tooltip>
    )
  }

  return (
    <Content>
      <SalesModal
        visibleSalesModal={visibleSalesModal}
        setVisibleSalesModal={setVisibleSalesModal}
        dateRange={dateRange}
      />
      <MisCostModal
        visibleMiscCostModal={visibleMiscCostModal}
        setVisibleMiscCostModal={setVisibleMiscCostModal}
        dateRange={dateRange}
      />
      <ForecasteddSalesModal
        visibleForecastModal={visibleForecastModal}
        setVisibleForecastModal={setVisibleForecastModal}
        dateRange={dateRange}
      />
      <NotesModal
        visibleNotesModal={visibleNotesModal}
        setVisibleNotesModal={setVisibleNotesModal}
        date={selectedNoteDate}
        existingNote={existingNote}
        dateRange={dateRange}
      />
      <PlanLimitModal
        showLimitModal={showLimitModal}
        setShowLimitModal={setShowLimitModal}
        variant='gpDataEntry'
      />
      <FoodSafetyHeroBand>
        <FoodSafetyHeaderRow>
          <div>
            <FoodSafetyPageTitle>Gross Profit</FoodSafetyPageTitle>
            <HeroIntro>See how purchasing affects your gross profit.</HeroIntro>
          </div>
          <VatToggle />
        </FoodSafetyHeaderRow>
        <HeroActions>
          <div>
            <TitleForRnage>Select date</TitleForRnage>
            <RangePicker
              onFocus={() => setVisibleCalendar(true)}
              onBlur={() => setVisibleCalendar(false)}
              open={visibleCalendar}
              disabledDate={disableEndDate}
              value={rangePickerValue}
              onChange={onChangeDate}
            />
          </div>
          <HeroButtons>
            <Button
              type='primary'
              onClick={() => {
                currentPlan === 'Free'
                  ? setShowLimitModal(true)
                  : setVisibleSalesModal(true)
              }}
            >
              Enter Revenue Data
            </Button>
            <Button
              onClick={() => {
                currentPlan === 'Free'
                  ? setShowLimitModal(true)
                  : setVisibleMiscCostModal(true)
              }}
            >
              Add Misc Costs
            </Button>
            <Button
              onClick={() => {
                currentPlan === 'Free'
                  ? setShowLimitModal(true)
                  : setVisibleForecastModal(true)
              }}
            >
              Add Forecasted Revenue
            </Button>
          </HeroButtons>
        </HeroActions>
      </FoodSafetyHeroBand>
      <EmailSettingsCard>
        <EmailSettingsHeader
          style={{ marginBottom: emailSettingsOpen ? 20 : 0 }}
        >
          <EmailSettingsTitle>
            <button
              type='button'
              aria-expanded={emailSettingsOpen}
              onClick={() => setEmailSettingsOpen(current => !current)}
              style={{
                background: 'none',
                border: 0,
                padding: 0,
                font: 'inherit',
                cursor: 'pointer',
                color: 'inherit',
              }}
            >
              {emailSettingsOpen ? '▾' : '▸'} Email Report Settings
            </button>
          </EmailSettingsTitle>
          {emailSettingsOpen ? (
            <EmailSettingsSubtitle>
              Configure automated email reports for Gross Profit data.
            </EmailSettingsSubtitle>
          ) : null}
        </EmailSettingsHeader>
        {emailSettingsOpen ? (
          <>
            <ReceiptingAccuracyNote>
              For GP and Spend reports to be accurate, all orders in Goods
              receipting must be reconciled. Purchases that have been received
              but are still awaiting invoice reconciliation are not yet included
              in Gross Profit — they appear once the invoice is matched and
              reconciled.{' '}
              <Link
                to={`/v/${venueId}/purchasing/receipting?filter=needs_receipting`}
              >
                Review outstanding receipting orders
              </Link>
              .
            </ReceiptingAccuracyNote>
            <EmailSettingsContent>
              <EmailSettingsRow>
                <EmailInputSection style={{ flex: 1, minWidth: 0 }}>
                  <EmailInputLabel>Email Recipients</EmailInputLabel>
                  <ReactMultiEmail
                    placeholder='Enter email addresses'
                    emails={gpReportEmails}
                    style={{ margin: '0px' }}
                    onChange={_emails => {
                      setGpReportEmails(_emails)
                    }}
                    validateEmail={email => isEmail(email)}
                    getLabel={(email, index, removeEmail) => (
                      <InnerDiv data-tag key={index}>
                        {email}
                        <span
                          data-tag-handle
                          onClick={() => removeEmail(index)}
                        >
                          ×
                        </span>
                      </InnerDiv>
                    )}
                  />
                </EmailInputSection>
                <EmailInputSection style={{ flexShrink: 0, width: 200 }}>
                  <EmailInputLabel>
                    Report Frequency{' '}
                    <Tooltip
                      title={
                        <>
                          <div>
                            <strong>Weekly:</strong> Last full Monday–Sunday.
                            Email sent Tuesday 9am.
                          </div>
                          <div>
                            <strong>Monthly:</strong> Previous calendar month.
                            Email sent on the 2nd at 9am (e.g. 2 March for
                            February’s report).
                          </div>
                        </>
                      }
                    >
                      <InfoCircleOutlined
                        style={{ marginLeft: 4, color: '#1890ff' }}
                      />
                    </Tooltip>
                  </EmailInputLabel>
                  <Select
                    value={gpReportFrequency}
                    onChange={value => setGpReportFrequency(value)}
                    style={{ width: '100%' }}
                  >
                    <Option value='none'>None</Option>
                    <Option value='weekly'>Weekly</Option>
                    <Option value='monthly'>Monthly</Option>
                  </Select>
                </EmailInputSection>
              </EmailSettingsRow>
              <SaveButtonWrapper>
                {showSaved ? (
                  <SavedIndicator>
                    <span>✓ Saved</span>
                  </SavedIndicator>
                ) : (
                  <Button type='primary' onClick={handleSaveEmailSettings}>
                    Save
                  </Button>
                )}
              </SaveButtonWrapper>
            </EmailSettingsContent>
          </>
        ) : null}
      </EmailSettingsCard>
      {canConfigureCategories ? (
        <RevenueCategoryList
          venueId={venueId}
          readOnly={currentPlan === 'Free'}
        />
      ) : null}
      {activeRevenueCategories.length > 0 && currentPlan !== 'Free' ? (
        <SalesSplit
          venueId={venueId}
          from={dateRange?.startDate}
          to={dateRange?.endDate}
        />
      ) : null}
      <AnalyticsCard>
        <AnalyticsHeader>
          <div>
            <AnalyticsTitle>Analytics</AnalyticsTitle>
            <AnalyticsSubtitle>
              Spend combines OMS spend and manual misc costs.
            </AnalyticsSubtitle>
          </div>
          <Button type='link' onClick={() => setIsAnalyticsOpen(prev => !prev)}>
            {isAnalyticsOpen ? <UpOutlined /> : <DownOutlined />}{' '}
            {isAnalyticsOpen ? 'Hide' : 'Show'} analytics
          </Button>
        </AnalyticsHeader>
        {isAnalyticsOpen && (
          <AnalyticsContent>
            <ControlsSection>
              <ControlBlock>
                <ControlLabel>Date range</ControlLabel>
                <ControlButtons>
                  {quickRanges.map(option => (
                    <Button
                      key={option.value}
                      size='small'
                      type={
                        activeQuickRange === option.value
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() => handleQuickRangeSelect(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </ControlButtons>
              </ControlBlock>
              <ControlBlock>
                <ControlLabel>View</ControlLabel>
                <Select
                  size='small'
                  value='daily'
                  style={{ width: 120 }}
                  disabled
                >
                  <Option value='daily'>Daily</Option>
                </Select>
              </ControlBlock>
              <ControlBlock>
                <ControlLabel>Compare</ControlLabel>
                <CompareToggles>
                  <ToggleItem>
                    <Switch
                      size='small'
                      checked={weekAverageEnabled}
                      onChange={setWeekAverageEnabled}
                    />{' '}
                    Week avg
                  </ToggleItem>
                  <ToggleItem>
                    <Switch
                      size='small'
                      checked={previousYearEnabled}
                      loading={previousYearStatus.loading}
                      onChange={checked => {
                        setPreviousYearEnabled(checked)
                        if (!checked) {
                          setPreviousYearStatus({ loading: false, error: null })
                        }
                      }}
                    />{' '}
                    Prev year
                  </ToggleItem>
                </CompareToggles>
              </ControlBlock>
              <ControlBlock>
                <ControlLabel>Metrics</ControlLabel>
                <Checkbox.Group
                  options={metricOptions}
                  value={selectedMetrics}
                  onChange={handleMetricChange}
                />
              </ControlBlock>
              <ControlBlock>
                <ControlLabel>Export</ControlLabel>
                <Button size='small' onClick={handleXLSXExport}>
                  <DownloadOutlined /> Download
                </Button>
              </ControlBlock>
            </ControlsSection>
            <ChartContainer>
              {legendEntries.length > 0 && (
                <AnalyticsLegend>
                  {legendEntries.map(entry => (
                    <LegendItem key={entry.key} $variant={entry.variant}>
                      <LegendDot
                        $color={entry.color}
                        $variant={entry.variant}
                      />
                      <LegendLabel $variant={entry.variant}>
                        {entry.label}
                      </LegendLabel>
                      {entry.showSpendNote && (
                        <LegendSubLabel>
                          (OMS spend + misc costs)
                        </LegendSubLabel>
                      )}
                    </LegendItem>
                  ))}
                </AnalyticsLegend>
              )}
              {previousYearStatus.error && (
                <LegendAlert
                  type='warning'
                  message={previousYearStatus.error}
                  showIcon
                  closable
                  onClose={() =>
                    setPreviousYearStatus({
                      loading: false,
                      error: null,
                    })
                  }
                />
              )}
              {isLoading ? (
                <ChartLoadingState>
                  <Spin tip='Loading analytics...' />
                </ChartLoadingState>
              ) : hasChartData ? (
                <ChartCanvasWrapper>
                  <ResponsiveLine
                    data={combinedSeries}
                    theme={chartTheme}
                    colors={({ id }) => colorBySerieId[id] || revenueColour}
                    margin={{
                      top: 32,
                      right: axisRightConfig ? 64 : 24,
                      bottom: 48,
                      left: onlyProfitSelected ? 56 : 72,
                    }}
                    xScale={{ type: 'point' }}
                    xFormat={value => dayjs(value).format('DD MMM YYYY')}
                    yScale={{
                      type: 'linear',
                      min: 0,
                      max: 'auto',
                      stacked: false,
                    }}
                    yFormat={value =>
                      onlyProfitSelected
                        ? formatPercent((value / (profitScaleBase || 1)) * 100)
                        : formatCurrency(value)
                    }
                    axisBottom={{
                      orient: 'bottom',
                      tickSize: 0,
                      tickPadding: 8,
                      tickRotation: analyticsData.length > 12 ? -25 : 0,
                      format: value => dayjs(value).format('DD MMM'),
                    }}
                    axisLeft={axisLeftConfig}
                    axisRight={axisRightConfig}
                    enableGridX={false}
                    enableGridY={true}
                    gridYValues={6}
                    lineWidth={3}
                    curve='monotoneX'
                    enablePoints={true}
                    pointSize={overlaySeriesIds.size ? 6 : 8}
                    pointBorderWidth={2}
                    pointBorderColor={({ serieId }) =>
                      colorBySerieId[serieId] || '#555'
                    }
                    pointColor='#FFFFFF'
                    enableArea={
                      overlaySeriesIds.size === 0 &&
                      selectedDefinitions.some(def => def.key !== 'profit')
                    }
                    areaOpacity={0.08}
                    useMesh={true}
                    crosshairType='x'
                    enableSlices='x'
                    tooltip={({ point }) => {
                      const definition = metricDefinitionByLabel[point.serieId]
                      const isProfitSeries =
                        point.serieId?.includes('Profit %') ||
                        definition?.key === 'profit'
                      const rawValue = point.data.rawValue
                      let formattedValue
                      if (isProfitSeries && rawValue != null) {
                        formattedValue = formatPercent(rawValue)
                      } else {
                        formattedValue =
                          point.data.formattedValue ||
                          (definition?.format && rawValue != null
                            ? definition.format(rawValue)
                            : null) ||
                          (rawValue != null
                            ? formatCurrency(rawValue)
                            : null) ||
                          point.data.yFormatted
                      }
                      return (
                        <ChartTooltip>
                          <TooltipDate>
                            {dayjs(point.data.x).format('dddd, DD MMM YYYY')}
                          </TooltipDate>
                          <TooltipValue>
                            <span
                              style={{
                                color:
                                  definition?.color ||
                                  colorBySerieId[point.serieId] ||
                                  '#222',
                              }}
                            >
                              {definition?.label ?? point.serieId}
                            </span>
                            <strong>{formattedValue}</strong>
                          </TooltipValue>
                        </ChartTooltip>
                      )
                    }}
                  />
                </ChartCanvasWrapper>
              ) : (
                <ChartEmptyState>
                  <Empty description='No analytics data in this date range yet.' />
                </ChartEmptyState>
              )}
            </ChartContainer>
            <NotesPlaceholder>
              {notesForDateRange.length > 0 ? (
                <div>
                  <NotesTitle>Notes:</NotesTitle>
                  <NotesList>
                    {notesForDateRange.map((note, index) => (
                      <NotesItem key={`${note.date}-${index}`}>
                        <strong>{note.displayDate}</strong> - {note.notes}
                      </NotesItem>
                    ))}
                  </NotesList>
                </div>
              ) : (
                <NotesEmptyState>
                  No notes for the selected date range.
                </NotesEmptyState>
              )}
            </NotesPlaceholder>
          </AnalyticsContent>
        )}
      </AnalyticsCard>
      {excludedThisPeriodSummary ? (
        <ExcludedThisPeriodNotice data-testid='gp-excluded-this-period'>
          <Tooltip title={GP_EXCLUDED_THIS_PERIOD_TOOLTIP}>
            <span>{excludedThisPeriodSummary}</span>
          </Tooltip>
        </ExcludedThisPeriodNotice>
      ) : null}
      <SummaryBar>
        <SummaryMetric>
          <Tooltip title="Average daily sales based on this period's entries so far.">
            <SummaryLabel>Avg. Revenue / Day</SummaryLabel>
          </Tooltip>
          <SummaryValue>
            {(() => {
              if (!totalSaleWeek || totalSaleWeek.length === 0) {
                return '£0.00 / day'
              }
              const avgSales =
                totalSaleWeek.length > 0
                  ? totalSaleAmount / totalSaleWeek.length
                  : 0
              const totalForecasted = totalSaleWeek.reduce(
                (sum, day) => sum + (day.forecastedSales || 0),
                0,
              )
              const avgForecasted =
                totalSaleWeek.length > 0
                  ? totalForecasted / totalSaleWeek.length
                  : 0
              const revenueColor =
                avgSales >= avgForecasted ? '#52c41a' : '#ff4d4f'
              return (
                <span style={{ color: revenueColor }}>
                  {formatCurrency(avgSales)} / day
                </span>
              )
            })()}
          </SummaryValue>
        </SummaryMetric>
        <SummaryMetric>
          <Tooltip title='Average daily forecasted revenue for this period.'>
            <SummaryLabel>Avg. Forecasted Revenue / Day</SummaryLabel>
          </Tooltip>
          <SummaryValue>
            {(() => {
              if (!totalSaleWeek || totalSaleWeek.length === 0) {
                return '£0.00 / day'
              }
              const totalForecasted = totalSaleWeek.reduce(
                (sum, day) => sum + (day.forecastedSales || 0),
                0,
              )
              const avgForecasted =
                totalSaleWeek.length > 0
                  ? totalForecasted / totalSaleWeek.length
                  : 0
              return `${formatCurrency(avgForecasted)} / day`
            })()}
          </SummaryValue>
        </SummaryMetric>
        <SummaryMetric>
          <Tooltip title={GP_OMS_SPEND_AVG_TOOLTIP}>
            <SummaryLabel>Avg. OMS Spend / Day</SummaryLabel>
          </Tooltip>
          <SummaryValue>
            {(() => {
              if (!totalSaleWeek || totalSaleWeek.length === 0) {
                return '£0.00 / day'
              }
              const totalOmsSpend = totalSaleWeek.reduce(
                (sum, day) => sum + pickGpOmsSpend(vatPreference, day),
                0,
              )
              const avgOmsSpend =
                totalSaleWeek.length > 0
                  ? totalOmsSpend / totalSaleWeek.length
                  : 0
              return `${formatCurrency(avgOmsSpend)} / day`
            })()}
          </SummaryValue>
        </SummaryMetric>
        <SummaryMetric>
          <Tooltip title='Average daily misc costs (manual entry) for this period.'>
            <SummaryLabel>Avg. Misc Costs / Day</SummaryLabel>
          </Tooltip>
          <SummaryValue>
            {(() => {
              if (!totalSaleWeek || totalSaleWeek.length === 0) {
                return '£0.00 / day'
              }
              const avgMisc =
                totalSaleWeek.length > 0
                  ? totalMiscCosts / totalSaleWeek.length
                  : 0
              return `${formatCurrency(avgMisc)} / day`
            })()}
          </SummaryValue>
        </SummaryMetric>
        <SummaryMetric>
          <Tooltip title='Average daily total spend (OMS + Misc costs) for this period.'>
            <SummaryLabel>Avg. Total Spend / Day</SummaryLabel>
          </Tooltip>
          <SummaryValue>
            {(() => {
              if (!totalSaleWeek || totalSaleWeek.length === 0) {
                return '£0.00 / day'
              }
              const totalSpend = totalSaleWeek.reduce(
                (sum, day) =>
                  sum +
                  pickGpOmsSpend(vatPreference, day) +
                  (day.miscCostPerDay || 0),
                0,
              )
              const avgTotalSpend =
                totalSaleWeek.length > 0 ? totalSpend / totalSaleWeek.length : 0
              return `${formatCurrency(avgTotalSpend)} / day`
            })()}
          </SummaryValue>
        </SummaryMetric>
        <SummaryMetric>
          <Tooltip title='Gross Profit % includes all costs (food cost + misc costs). Shows green when ≥ 75% (good performance), red when < 75% (needs attention).'>
            <SummaryLabel>GP %</SummaryLabel>
          </Tooltip>
          <SummaryValue
            style={{
              color: parseFloat(GP || '0') >= 75 ? '#52c41a' : '#ff4d4f',
            }}
          >
            {GP || '0.0'}%
          </SummaryValue>
        </SummaryMetric>
        <SummaryMetric>
          <Tooltip title='Gross Profit % excluding misc costs (food profit only). This shows your food margin before wages, utilities, etc. Shows green when ≥ 75% (good performance), red when < 75% (needs attention).'>
            <SummaryLabel>GP % (Excl. Misc Costs)</SummaryLabel>
          </Tooltip>
          <SummaryValue>
            {(() => {
              const gpExclMiscPercent = GPExclMisc || '0.0'
              const gpExclMiscValue = parseFloat(gpExclMiscPercent)
              // Calculate £ value: (Sales - Food Cost)
              const totalOmsSpend =
                totalSaleWeek?.reduce(
                  (sum, day) => sum + (day.purchasesOnOMS || 0),
                  0,
                ) || 0
              const profitExclMisc = totalSaleAmount - totalOmsSpend
              return (
                <>
                  <div
                    style={{
                      color: gpExclMiscValue >= 75 ? '#52c41a' : '#ff4d4f',
                    }}
                  >
                    {gpExclMiscPercent}%
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#6b6b6b',
                      marginTop: '2px',
                    }}
                  >
                    {formatCurrency(profitExclMisc)}
                  </div>
                </>
              )
            })()}
          </SummaryValue>
        </SummaryMetric>
        <SummaryMetric>
          <Tooltip title='Misc Costs as a percentage of total sales (wages, utilities, etc.). Shows red when ≥ 45% (high relative to revenue), green when < 45% (acceptable level).'>
            <SummaryLabel>Misc Costs %</SummaryLabel>
          </Tooltip>
          <SummaryValue>
            {(() => {
              if (
                !totalSaleWeek ||
                totalSaleWeek.length === 0 ||
                !totalSaleAmount
              ) {
                return '0.0%'
              }
              const miscCostsPercent =
                totalSaleAmount > 0
                  ? ((totalMiscCosts / totalSaleAmount) * 100).toFixed(1)
                  : '0.0'
              const miscCostsValue = parseFloat(miscCostsPercent)
              return (
                <span
                  style={{
                    color: miscCostsValue >= 45 ? '#ff4d4f' : '#52c41a',
                  }}
                >
                  {miscCostsPercent}%
                </span>
              )
            })()}
          </SummaryValue>
        </SummaryMetric>
        <SummaryMetric>
          <Tooltip title='OMS Spend (food costs) as a percentage of total sales. Shows red when ≥ 30% (high food costs relative to revenue), green when < 30% (acceptable level).'>
            <SummaryLabel>OMS Spend %</SummaryLabel>
          </Tooltip>
          <SummaryValue>
            {(() => {
              if (
                !totalSaleWeek ||
                totalSaleWeek.length === 0 ||
                !totalSaleAmount
              ) {
                return '0.0%'
              }
              const totalOmsSpend = totalSaleWeek.reduce(
                (sum, day) => sum + pickGpOmsSpend(vatPreference, day),
                0,
              )
              const omsSpendPercent =
                totalSaleAmount > 0
                  ? ((totalOmsSpend / totalSaleAmount) * 100).toFixed(1)
                  : '0.0'
              const omsSpendValue = parseFloat(omsSpendPercent)
              return (
                <span
                  style={{
                    color: omsSpendValue >= 30 ? '#ff4d4f' : '#52c41a',
                  }}
                >
                  {omsSpendPercent}%
                </span>
              )
            })()}
          </SummaryValue>
        </SummaryMetric>
      </SummaryBar>
      <Wrapper>
        <Table
          dataSource={totalSaleWeek}
          expandedRowRender={record => renderPurchases(record)}
          onExpand={(expanded, record) => {
            if (!expanded || !venueId || !record?.date) return
            const date = dayjs(record.date).format('YYYY-MM-DD')
            setExpandedRevenue(current => ({
              ...current,
              [date]: { loading: true, lines: current[date]?.lines || [] },
            }))
            readRevenueDay(venueId, date)
              .then(day => {
                setExpandedRevenue(current => ({
                  ...current,
                  [date]: {
                    loading: false,
                    lines: Array.isArray(day?.revenueLines)
                      ? day.revenueLines
                      : [],
                  },
                }))
                return null
              })
              .catch(() => {
                setExpandedRevenue(current => ({
                  ...current,
                  [date]: { loading: false, lines: [] },
                }))
              })
          }}
          pagination={{
            position: 'both',
            hideOnSinglePage: true,
            showSizeChanger: false,
          }}
          scroll={RESPONSIVE_TABLE_SCROLL}
          // loading={dataSource.length < 1}
          columns={[
            {
              title: 'Date',
              dataIndex: 'date',
              key: 'date',
              className: 'restrictShrink',
              sorter: (a, b) =>
                String(a.product)
                  .trim()
                  .localeCompare(String(b.product).trim()),
              sortDirections: ['ascend', 'descend'],
            },
            {
              title: 'Day',
              dataIndex: 'day',
              key: 'day',
              className: 'restrictShrink',
              sorter: (a, b) =>
                String(a.product)
                  .trim()
                  .localeCompare(String(b.product).trim()),
              sortDirections: ['ascend', 'descend'],
            },
            {
              title: 'Revenue',
              dataIndex: 'sales',
              key: 'sales',
              className: 'restrictShrink',
              sorter: (a, b) => a.quantity - b.quantity,
              sortDirections: ['ascend', 'descend'],
              render: (text, record) =>
                revenueCategories == null
                  ? formatCurrency(text)
                  : activeRevenueCategories.length
                  ? renderCategoryRevenueCell(record, text)
                  : renderInlineEditableCurrencyCell(record, 'sales', text),
            },
            {
              title: (
                <>
                  Misc Costs (manual entry){' '}
                  <Tooltip title='Use this to record costs that aren’t placed through OMS — e.g. electricity bills, water deliveries, or staff meals.'>
                    <InfoCircleOutlined />
                  </Tooltip>
                </>
              ),
              dataIndex: 'miscCostPerDay',
              key: 'miscCostPerDay',
              className: 'restrictShrink',
              sorter: (a, b) => a.quantity - b.quantity,
              sortDirections: ['ascend', 'descend'],
              render: (text, record) =>
                renderInlineEditableCurrencyCell(
                  record,
                  'miscCostPerDay',
                  text,
                ),
            },

            {
              title: (
                <>
                  {`OMS Spend (auto ${vatLabelSuffix(vatPreference)})`}{' '}
                  <Tooltip title={GP_OMS_SPEND_COLUMN_TOOLTIP}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </>
              ),
              dataIndex: 'purchasesOnOMS',
              key: 'purchasesOnOMS',
              className: 'restrictShrink',
              render: (_, record) =>
                formatCurrency(pickGpOmsSpend(vatPreference, record)),
            },
            {
              title: 'Forecasted Revenue',
              dataIndex: 'forecastedSales',
              key: 'forecastedSales',
              className: 'restrictShrink',
              sorter: (a, b) => a.quantity - b.quantity,
              sortDirections: ['ascend', 'descend'],
              render: (text, record) =>
                renderInlineEditableCurrencyCell(
                  record,
                  'forecastedSales',
                  text,
                ),
            },
            {
              title: (
                <>
                  Variance{' '}
                  <Tooltip title='Variance = Actual sales minus forecasted sales. Shows green when ≥ 0 (sales met or exceeded forecast), red when < 0 (sales below forecast).'>
                    <InfoCircleOutlined />
                  </Tooltip>
                </>
              ),
              key: 'variance',
              className: 'restrictShrink',
              sorter: (a, b) =>
                a.forecastedSales - a.sales - (b.forecastedSales - b.sales),
              sortDirections: ['ascend', 'descend'],
              render: (_, record) => {
                const variance = record.sales - record.forecastedSales
                const varianceColor = variance >= 0 ? '#52c41a' : '#ff4d4f'
                return (
                  <span style={{ color: varianceColor, fontWeight: 'bold' }}>
                    {formatCurrency(variance)}
                  </span>
                )
              },
            },
            {
              title: 'Notes',
              dataIndex: 'notes',
              key: 'notes',
              className: 'restrictShrink',
              width: '15%',
              align: 'center',
              render: (notes, record) => {
                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {notes && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#666',
                          marginBottom: '4px',
                          textAlign: 'center',
                        }}
                        title={notes}
                      >
                        {truncateText(notes, 50)}
                      </div>
                    )}
                    <Button
                      type='link'
                      size='small'
                      onClick={() => {
                        setSelectedNoteDate(record.date)
                        setExistingNote(notes || null)
                        setVisibleNotesModal(true)
                      }}
                      style={{ padding: 0, height: 'auto' }}
                    >
                      {notes ? 'Edit Note' : 'Add Note'}
                    </Button>
                  </div>
                )
              },
            },
            {
              title: 'Delete Data',
              dataIndex: 'id',
              className: 'restrictShrink',
              key: 'id',
              width: '10%',
              render: id => {
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-around',
                      alignItems: 'center',
                    }}
                  >
                    <Popconfirm
                      title={
                        id === ''
                          ? 'Please enter revenue data first'
                          : 'Are you sure you want to delete the revenue data?'
                      }
                      placement='topRight'
                      onConfirm={() => {
                        if (id !== '') {
                          dispatch(
                            deleteSalesOrPurchaseData(id, dateRange, venueId),
                          )
                        }
                      }}
                      okText={id === '' ? 'OK' : 'Yes'}
                      cancelText={id === '' ? 'Cacnel' : 'No'}
                    >
                      <StyledButton type={'danger'}>
                        <DeleteOutlined />
                      </StyledButton>
                    </Popconfirm>
                  </div>
                )
              },
            },
          ]}
          footer={() => {
            const totalOmsSpend = (totalSaleWeek || []).reduce(
              (sum, record) => sum + pickGpOmsSpend(vatPreference, record),
              0,
            )
            const totalForecasted = (totalSaleWeek || []).reduce(
              (sum, record) => sum + (record?.forecastedSales ?? 0),
              0,
            )
            return (
              <Row>
                <Row>
                  <Col span={19}></Col>
                  <Col span={3}>
                    <strong>Total GP:</strong>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      color:
                        parseFloat(GP || '0') >= 75 ? '#52c41a' : '#ff4d4f',
                      fontWeight: 'bold',
                    }}
                  >
                    {GP}%
                  </Col>
                </Row>
                <Row>
                  <Col span={19}></Col>
                  <Col span={3}>
                    <strong>Total GP (Excl. Misc Costs):</strong>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      color:
                        parseFloat(GPExclMisc || '0') >= 75
                          ? '#52c41a'
                          : '#ff4d4f',
                      fontWeight: 'bold',
                    }}
                  >
                    {GPExclMisc || '0.0'}%
                  </Col>
                </Row>
                <Row>
                  <Col span={19}></Col>
                  <Col span={3}>
                    <strong>Misc Costs %:</strong>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      color:
                        totalSaleAmount > 0 &&
                        (totalMiscCosts / totalSaleAmount) * 100 >= 45
                          ? '#ff4d4f'
                          : '#52c41a',
                      fontWeight: 'bold',
                    }}
                  >
                    {totalSaleAmount > 0
                      ? ((totalMiscCosts / totalSaleAmount) * 100).toFixed(1)
                      : '0.0'}
                    %
                  </Col>
                </Row>
                <Row>
                  <Col span={19}></Col>
                  <Col span={3}>
                    <strong>OMS Spend %:</strong>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      color:
                        totalSaleAmount > 0 &&
                        (totalOmsSpend / totalSaleAmount) * 100 >= 30
                          ? '#ff4d4f'
                          : '#52c41a',
                      fontWeight: 'bold',
                    }}
                  >
                    {totalSaleAmount > 0
                      ? ((totalOmsSpend / totalSaleAmount) * 100).toFixed(1)
                      : '0.0'}
                    %
                  </Col>
                </Row>
                <Row>
                  <Col span={19}></Col>
                  <Col span={3}>
                    <strong>Total Misc Costs:</strong>
                  </Col>
                  <Col span={2}>{formatCurrency(totalMiscCosts)}</Col>
                </Row>
                <Row>
                  <Col span={19}></Col>
                  <Col span={3}>
                    <strong>{`Total OMS Spend ${vatLabelSuffix(
                      vatPreference,
                    )}:`}</strong>
                  </Col>
                  <Col span={2}>{formatCurrency(totalOmsSpend)}</Col>
                </Row>
                <Row>
                  <Col span={19}></Col>
                  <Col span={3}>
                    <strong>Total Forecasted Revenue:</strong>
                  </Col>
                  <Col span={2}>{formatCurrency(totalForecasted)}</Col>
                </Row>
                <Row>
                  <Col span={19}></Col>
                  <Col span={3}>
                    <strong>Total Revenue:</strong>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      color:
                        totalSaleAmount >= totalForecasted
                          ? '#52c41a'
                          : '#ff4d4f',
                      fontWeight: 'bold',
                    }}
                  >
                    {formatCurrency(totalSaleAmount)}
                  </Col>
                </Row>
              </Row>
            )
          }}
        />
      </Wrapper>
    </Content>
  )
}

const EmailSettingsCard = styled.section`
  background: #ffffff;
  border: 1px solid #e4efc8;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 2px rgba(19, 45, 7, 0.04);
`

const EmailSettingsHeader = styled.div`
  margin-bottom: 20px;
`

const EmailSettingsTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 600;
`

const EmailSettingsSubtitle = styled.p`
  margin: 0;
  color: #6b6b6b;
  font-size: 13px;
`

const ReceiptingAccuracyNote = styled.p`
  margin: 0 0 14px 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  color: #614700;
  font-size: 13px;
  line-height: 1.5;

  a {
    color: #389e0d;
    font-weight: 600;
  }
`

const EmailSettingsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const EmailSettingsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: flex-end;
`

const EmailInputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const EmailInputLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`

const SaveButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`

const SavedIndicator = styled.div`
  padding: 8px 16px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  color: #52c41a;
  font-weight: 500;
  font-size: 14px;
`

const InnerDiv = styled.div`
  background-color: ${({ theme }) => theme.colours.lettuceGreen} !important;
`

const AnalyticsCard = styled.section`
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`

const AnalyticsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`

const AnalyticsTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
`

const AnalyticsSubtitle = styled.p`
  margin: 4px 0 0;
  color: #6b6b6b;
  font-size: 13px;
`

const AnalyticsContent = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const ControlsSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`

const ControlBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ControlLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b6b6b;
`

const ControlButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const CompareToggles = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`

const ToggleItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
`

const ChartContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const AnalyticsLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
`

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4f4f4f;
  opacity: ${({ $variant }) => ($variant === 'prevYear' ? 0.85 : 1)};
`

const LegendDot = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 999px;
  display: inline-block;
  background: ${({ $variant, $color }) =>
    $variant === 'weekAvg' ? 'transparent' : $color};
  border: ${({ $variant, $color }) =>
    $variant === 'weekAvg' ? `2px solid ${$color}` : 'none'};
  opacity: ${({ $variant }) => ($variant === 'prevYear' ? 0.75 : 1)};
`

const LegendLabel = styled.span`
  font-weight: ${({ $variant }) => ($variant === 'solid' ? 600 : 500)};
  color: ${({ $variant }) => ($variant === 'prevYear' ? '#333' : '#212121')};
`

const LegendSubLabel = styled.span`
  font-size: 12px;
  color: #828282;
`

const LegendAlert = styled(Alert)`
  margin-top: 12px;
  border-radius: 8px;
  padding: 8px 12px;
`

const ChartCanvasWrapper = styled.div`
  width: 100%;
  height: 320px;
`

const ChartLoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 320px;
  background: #fafafa;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
`

const ChartEmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 320px;
  background: #fafafa;
  border-radius: 12px;
  border: 1px dashed rgba(0, 0, 0, 0.1);
`

const NotesPlaceholder = styled.div`
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  color: #6b6b6b;
  font-size: 13px;
`

const NotesTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`

const NotesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const NotesItem = styled.li`
  margin-bottom: 8px;
  padding-left: 0;
  color: #4f4f4f;
  line-height: 1.5;
  &:before {
    content: '•';
    color: #1890ff;
    font-weight: bold;
    display: inline-block;
    width: 1em;
    margin-right: 8px;
  }
  strong {
    color: #333;
    font-weight: 600;
  }
`

const NotesEmptyState = styled.div`
  color: #8c8c8c;
  font-style: italic;
  text-align: center;
`

const SummaryBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 32px;
  padding: 20px 24px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`

const ExcludedThisPeriodNotice = styled.div`
  margin: 0 0 12px;
  padding: 10px 16px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  color: #237804;
  font-size: 13px;
  text-align: center;
  cursor: help;
`

const SummaryMetric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
  align-items: center;
  text-align: center;
`

const SummaryLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #6b6b6b;
  text-transform: uppercase;
  cursor: help;
`

const SummaryValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
`

const ChartTooltip = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 10px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`

const TooltipDate = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #333333;
`

const TooltipValue = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 13px;
  color: #4f4f4f;
  span {
    font-weight: 600;
  }
  strong {
    color: #1a1a1a;
  }
`

const StyledButton = styled(Button)`
  margin: 3px 3px 3px 0;
  display: flex;
  justify-content: center;
  align-items: center;
`
const Content = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`

const HeroIntro = styled.p`
  margin: 8px 0 0;
  color: #555;
  font-size: 0.95rem;
  line-height: 1.45;
`

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-top: 16px;
`

const HeroButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const TitleForRnage = styled.strong`
  margin-right: 20px;
`

const Wrapper = styled(FadeIn)`
  .ant-table-thead {
    white-space: nowrap;
  }
  .ant-table-tbody {
    .restrictShrink {
      min-width: 90px;
      background-color: white;
    }
  }
`

export default GrossProfit
